import { Inject, inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { defaultParams, getParamValue } from '../utils';
import { TranslocoService } from '@jsverse/transloco';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { filter } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

import { Meta } from '@angular/platform-browser';
import { FooterItem } from '../../layout/footer/footer.component';
import { ProductMenuSideContainerComponent } from '@shared/components/core/mega-menu/menu-side-container/product-menu-side-container/product-menu-side-container.component';
import { AboutMenuSideContainerComponent } from '@shared/components/core/mega-menu/menu-side-container/about-menu-side-container/about-menu-side-container.component';
import { NavbarItem } from '../../models/navbar';
import { EnvironmentService } from './environment.service';
import { TrackingService } from './tracking.service';


@Injectable({
  providedIn: 'root',
})
export class WebsiteService {
  translateService = inject(TranslocoService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  cookieService = inject(SsrCookieService);
  meta = inject(Meta);
  environmentService = inject(EnvironmentService);
  currentRoute: string = '';

  get language() {
    return this.cookieService.get('language') || 'ar';
  }

  private _navbarItems: NavbarItem[] = [];

  get navbarItems() {
    return this._navbarItems;
  }

  private _footerItems!: { [key: string]: FooterItem[] };

  get footerItems() {
    return this._footerItems;
  }

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private trackingService: TrackingService) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.handleMetaTags();
        const segments = event.urlAfterRedirects.split('/');
        let language = segments[1]; // First path segment for languages
        this.currentRoute = event.urlAfterRedirects;

        if (language?.includes('?')) {
          language = language.split('?')[0];
        }

        const isValidLanguage = ['en', 'ar'].includes(language);

        if (language && isValidLanguage) {
          this.updateLanguageCookie(language);
          this.translateService.setActiveLang(this.language);
          this.translateService.setDefaultLang(this.language);
          this.setNavbarItems(language);
          this.setFooterItems(language);

          if (isPlatformBrowser(this.platformId)) {
            this.setLayoutDirection();
          }
        }
      });

    this.router.events
      .pipe(filter((e) => e instanceof NavigationStart))
      .subscribe((event: any) => {
        const url = event.url;
        if (!url.includes('customer-document-upload')) {
          const segments = url.split('/').filter(Boolean);
          const firstSegment = segments[0];
          const isValidLanguage = ['en', 'ar'].includes(firstSegment);

          if (!isValidLanguage) {
            const newUrl = ['/', this.language, ...segments].join('/');
            this.router.navigateByUrl(newUrl);
          }
        }
      });
  }
  private setFooterItems(language: string) {
    this._footerItems = {
      accountItems: [
        {
          id: 'footer-account',
          type: 'internal-link',
          label: 'ITEM1',
          value: `/${language}/products/account/sme-account`,
        },
        {
          id: 'footer-freelancer-account',
          type: 'internal-link',
          label: 'ITEM2',
          value: `/${language}/products/account/freelancer-account`,
        },
        {
          id: 'footer-multi-account',
          type: 'internal-link',
          label: 'ITEM3',
          value: `/${language}/products/account/multi-user-account`,
        },
      ],
      makePaymentItems: [
        {
          id: 'footer-debit-card',
          type: 'internal-link',
          label: 'ITEM1',
          value: `/${language}/products/payments/hala-debit-card`,
        },
        {
          id: 'footer-expense-management',
          type: 'internal-link',
          label: 'ITEM2',
          value: `/${language}/products/expense-management`,
        },
        {
          id: 'footer-sadad',
          type: 'internal-link',
          label: 'ITEM3',
          value: `/${language}/products/payments/sadad`,
        },
        {
          id: 'footer-local-transfers',
          type: 'internal-link',
          label: 'ITEM4',
          value: `/${language}/products/payments/local-transfers`,
        },
        {
          id: 'footer-wallet-to-wallet',
          type: 'internal-link',
          label: 'ITEM5',
          value: `/${language}/products/payments/wallet-to-wallet`,
        },
      ],
      getPaidItems: [
        {
          id: 'footer-payment-terminal',
          type: 'internal-link',
          label: 'ITEM1',
          value: `/${language}/products/payments/payment-terminal`,
        },
        {
          id: 'footer-soft-pos',
          type: 'internal-link',
          label: 'ITEM2',
          value: `/${language}/products/soft-pos`,
        },
        {
          id: 'footer-local',
          type: 'internal-link',
          label: 'ITEM3',
          value: `/${language}/products/payments/local-transfers`,
        },
        {
          id: 'footer-wallet',
          type: 'internal-link',
          label: 'ITEM4',
          value: `/${language}/products/payments/wallet-to-wallet`,
        },
      ],
      runBusinessItems: [
        {
          id: 'footer-financing',
          type: 'internal-link',
          label: 'ITEM1',
          value: `/${language}/products/financing`,
        },
        {
          id: 'footer-expense',
          type: 'internal-link',
          label: 'ITEM2',
          value: `/${language}/products/expense-management`,
        },
      ],
      callUsItems: [
        {
          id: 'footer-call1',
          type: 'tel',
          label: 'ITEM2',
          value: 'ITEM2',
        },
        {
          id: 'footer-call2',
          type: 'tel',
          label: 'ITEM3',
          value: 'ITEM3',
        },
        {
          id: 'footer-call3',
          type: 'text',
          label: 'ITEM4',
          value: 'ITEM4',
        },
        {
          id: 'footer-call4',
          type: 'tel',
          label: 'ITEM1',
          value: 'ITEM1',
        },
      ],
      customerCareItems: [
        {
          id: 'footer-contact-us',
          type: 'internal-link',
          label: 'ITEM1',
          value: `/${language}/contact-us`,
        },
        {
          id: 'footer-mail',
          type: 'mailto',
          label: 'ITEM2',
          value: 'ITEM2',
        },
      ],
      legacyItems: [
        {
          id: 'footer-cookie-policy',
          type: 'external-link',
          label: 'ITEM1',
          value: `/static/cookie-policy-${language}.html`,
        },
        {
          id: 'footer-privacy-policy',
          type: 'external-link',
          label: 'ITEM2',
          value: `/static/privacy-policy-${language}.html`,
        },
        // {
        //   type: 'download',
        //   label: 'ITEM5',
        //   value: '/documents/hala-payments-customer-protection-policy-ar.pdf',
        // },
        {
          id: 'footer-tcs-merchant',
          type: 'external-link',
          label: 'ITEM3',
          value: `/static/tcs-merchants-${language}.html`,
        },
        {
          id: 'footer-tcs-indiviual',
          type: 'external-link',
          label: 'ITEM4',
          value: `/static/tcs-individuals-${language}.html`,
        },
      ],
      connectWithUsItems: [
        {
          id: 'footer-about-s',
          type: 'internal-link',
          label: 'ITEM1',
          value: `/${language}/about-us`,
        },
        {
          id: 'footer-culture',
          type: 'internal-link',
          label: 'ITEM2',
          value: `/${language}/our-culture`,
        },
        {
          id: 'footer-career',
          type: 'external-link',
          value: 'https://job-boards.greenhouse.io/hala',
          label: 'ITEM3',
        },
      ],
    };
  }

  initiazeAppSettings() {
    this.translateService.setAvailableLangs(['en', 'ar']);
  }

  private handleMetaTags() {
    let route = this.route;
    while (route.firstChild) route = route.firstChild;
    const data = route.snapshot.data;

    if (data['META_DESCRIPTION']) {
      const metaDescription = data['META_DESCRIPTION'];
      this.translateService
        .selectTranslateObject(metaDescription)
        .subscribe((metaDescription) => {
          if (isPlatformBrowser(this.platformId)) {
            this.meta.updateTag({
              name: 'description',
              content: metaDescription.toString(),
            });
          }
        });
    }
  }

  private setNavbarItems(language: string) {
    const isActive = (routeLink: string): boolean => {
      return this.currentRoute === routeLink;
    };

    this._navbarItems = [
      {
        id: 'nav-accounts-main',
        label: 'HEADER.NAVS.PRODUCTS.MAIN',
        isParent: true,
        isHovered: false,
        customTemplate: ProductMenuSideContainerComponent,
        children: [
          {
            id: 'nav-accounts',
            label: 'HEADER.NAVS.PRODUCTS.ACCOUNTS',
            isParent: true,
            isHovered: false,
            children: [
              {
                id: 'nav-bank-account',
                label: 'HEADER.NAVS.PRODUCTS.BANK_ACCOUNT',
                routeLink: `/${language}/products/account/sme-account`,
                isActive: isActive(`/${language}/products/account/sme-account`)
              },
              {
                id: 'nav-freelancer-account',
                label: 'HEADER.NAVS.PRODUCTS.FREELANCER_ACCOUNT',
                routeLink: `/${language}/products/account/freelancer-account`,
                isActive: isActive(`/${language}/products/account/freelancer-account`)
              },
              {
                id: 'nav-multi-account',
                label: 'HEADER.NAVS.PRODUCTS.MULTI_USER_ACCOUNT',
                routeLink: `/${language}/products/account/multi-user-account`,
                isActive: isActive(`/${language}/products/account/multi-user-account`)
              },
              {
                id: 'nav-home-static',
                label: 'Home Static',
                routeLink: `/${language}/static-demo`,
                isActive: isActive(`/${language}/static-demo`)
              },
            ],
          },
          {
            id: 'nav-make-payments',
            label: 'HEADER.NAVS.PRODUCTS.MAKE_PAYMENTS',
            isParent: true,
            isHovered: false,
            children: [
              {
                id: 'nav-debit-card',
                label: 'HEADER.NAVS.PRODUCTS.DEBIT_CARD',
                routeLink: `/${language}/products/payments/hala-debit-card`,
                isActive: isActive(`/${language}/products/payments/hala-debit-card`)
              },
              {
                id: 'nav-expense-card',
                label: 'HEADER.NAVS.PRODUCTS.EXPENSE_CARD',
                routeLink: `/${language}/products/expense-management`,
                isActive: isActive(`/${language}/products/expense-management`)
              },
              {
                id: 'nav-sadad',
                label: 'HEADER.NAVS.PRODUCTS.SADAD',
                routeLink: `/${language}/products/payments/sadad`,
                isActive: isActive(`/${language}/products/payments/sadad`)
              },
              // {
              //   label: 'HEADER.NAVS.PRODUCTS.MUDAD',
              //   routeLink: `/${language}/products/payments/mudad`,
              //   isActive: isActive(`/${language}/products/payments/mudad`)
              // },
              {
                id: 'nav-local-transfers',
                label: 'HEADER.NAVS.PRODUCTS.LOCAL_TRANSFERS',
                routeLink: `/${language}/products/payments/local-transfers`,
                isActive: isActive(`/${language}/products/payments/local-transfers`)
              },
              {
                id: 'nav-wallet-to-wallet',
                label: 'HEADER.NAVS.PRODUCTS.WALLET_TO_WALLET',
                routeLink: `/${language}/products/payments/wallet-to-wallet`,
                isActive: isActive(`/${language}/products/payments/wallet-to-wallet`)
              },
            ],
          },
          {
            id: 'nav-get-paid',
            label: 'HEADER.NAVS.PRODUCTS.GET_PAID',
            isParent: true,
            isHovered: false,
            children: [
              {
                id: 'nav-pos-terminal',
                label: 'HEADER.NAVS.PRODUCTS.POS_TERMINAL',
                routeLink: `/${language}/products/payments/payment-terminal`,
                isActive: isActive(`/${language}/products/payments/payment-terminal`)
              },
              {
                id: 'nav-phone-pos',
                label: 'HEADER.NAVS.PRODUCTS.PHONE_POS',
                routeLink: `/${language}/products/soft-pos`,
                isActive: isActive(`/${language}/products/soft-pos`)
              },
              {
                id: 'nav-iban',
                label: 'HEADER.NAVS.PRODUCTS.IBAN_DEPOSITS',
                routeLink: `/${language}/products/payments/local-transfers`,
                isActive: isActive(`/${language}/products/payments/local-transfers`)
              },
              {
                id: 'nav-wallet-dep',
                label: 'HEADER.NAVS.PRODUCTS.WALLET_DEPOSITS',
                routeLink: `/${language}/products/payments/wallet-to-wallet`,
                isActive: isActive(`/${language}/products/payments/wallet-to-wallet`)
              },
            ],
          },
          {
            id: 'nav-run-business',
            label: 'HEADER.NAVS.PRODUCTS.RUN_YOUR_BUSINESS',
            isParent: true,
            isHovered: false,
            children: [
              {
                id: 'nav-finance',
                label: 'HEADER.NAVS.PRODUCTS.POS_FINANCING',
                routeLink: `/${language}/products/financing`,
                isActive: isActive(`/${language}/products/financing`)
              },
              {
                id: 'nav-exp',
                label: 'HEADER.NAVS.PRODUCTS.EXPENSE_MANAGMENT',
                routeLink: `/${language}/products/expense-management`,
                isActive: isActive(`/${language}/products/expense-management`)
              },
            ],
          },
        ],
      },
      {
        id: 'nav-about-us-main',
        label: 'HEADER.NAVS.ABOUT_US.MAIN',
        isParent: true,
        isHovered: false,
        customTemplate: AboutMenuSideContainerComponent,
        children: [
          {
            id: 'nav-about-u',
            label: 'HEADER.NAVS.ABOUT_US.MAIN',
            isParent: true,
            isHovered: false,
            children: [
              {
                id: 'nav-story',
                label: 'HEADER.NAVS.ABOUT_US.OUR_STORY',
                routeLink: `/${language}/about-us`,
                isActive: isActive(`/${language}/about-us`)
              },
              {
                id: 'nav-culture',
                label: 'HEADER.NAVS.ABOUT_US.OUR_CULTURE',
                routeLink: `/${language}/our-culture`,
                isActive: isActive(`/${language}/our-culture`)
              },
              {
                id: 'nav-career',
                label: 'HEADER.NAVS.ABOUT_US.CAREERS',
                isExternal: true,
                routeLink: 'https://job-boards.greenhouse.io/hala',
              },
              {
                id: 'nav-contact',
                label: 'HEADER.NAVS.ABOUT_US.CONTACT_US',
                routeLink: `/${language}/contact-us`,
                isActive: isActive(`/${language}/contact-us`),
              },
            ],
          },
        ],
      },
    ];
  }

  switchLanguage(): void {
    const activeLanguage = this.translateService.getActiveLang();

    if (activeLanguage === 'en') {
      this.updateLanguageCookie('ar');
      this.setNavbarItems('ar');
      this.setFooterItems('ar');
      this.translateService.setActiveLang('ar');
    } else if (activeLanguage === 'ar') {
      this.updateLanguageCookie('en');
      this.setNavbarItems('en');
      this.setFooterItems('en');
      this.translateService.setActiveLang('en');
    }

    const url = new URL(window.location.href);
    url.pathname = `/${[
      this.language,
      ...url.pathname.split('/').filter(Boolean).slice(1),
    ].join('/')}`;
    window.history.replaceState({}, '', url.toString());
    window.scrollTo(0, 0);
    this.setLayoutDirection();
  }

  setLayoutDirection() {
    const dir = this.language === 'ar' ? 'rtl' : 'ltr';

    document.getElementsByTagName('html')[0].setAttribute('dir', dir);
    document
      .getElementsByTagName('html')[0]
      .setAttribute('lang', this.language);
  }

  private updateLanguageCookie(language: string): void {
    // remove the old cookie
    this.cookieService.delete('language');
    // set the new cookie
    this.cookieService.set('language', language, 0, '/');
  }

  redirectToRoute(
    event_name: string,
    routeUrl: string,
    isOnBoarding = false,
    onBoardingUrl: string = 'https://signup-stg.hala.com/sign-up/business-type',
    fragment?: string
  ): void {
    this.route.queryParams.subscribe((params) => {
      let queryParams = new URLSearchParams(params);

      const agentCode = getParamValue(
        params,
        'agentcode',
        'agentcode',
        defaultParams.agentcode
      );

      const utmId = getParamValue(
        params,
        'utm_id',
        'campaignId',
        defaultParams.utm_id
      );
      const utmSource = getParamValue(
        params,
        'utm_source',
        'campaignSource',
        defaultParams.utm_source
      );
      const utmMedium = getParamValue(
        params,
        'utm_medium',
        '',
        defaultParams.utm_medium
      );
      const utmTerm = getParamValue(params, 'utm_term', 'campaignTerm', '');
      const utmCampaign = getParamValue(
        params,
        'utm_campaign',
        'campaignName',
        defaultParams.utm_campaign
      );

      const utm_query = getParamValue(
        params,
        'utm_query',
        'utm_query',
        defaultParams.utm_query
      );

      const utm_content = getParamValue(
        params,
        'utm_content',
        'utm_content',
        defaultParams.utm_content
      );

      const fbclid = getParamValue(
        params,
        'fbclid',
        'fbclid',
        defaultParams.fbclid
      );

      const gclid = getParamValue(
        params,
        'gclid',
        'gclid',
        defaultParams.gclid
      );

      const source = params['source'] || localStorage.getItem('source') || null;

      this.trackingService.logEvent(event_name, {
        device_id: this.getDeviceId(),
        user_id: this.getUserId(), // Replace with user ID
        utm_id: utmId,
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_term: utmTerm,
        utm_campaign: utmCampaign,
      });

      queryParams.set('agentCode', agentCode);
      queryParams.set('utm_id', utmId);
      queryParams.set('utm_source', utmSource);
      queryParams.set('utm_medium', utmMedium);
      queryParams.set('utm_term', utmTerm);
      queryParams.set('utm_campaign', utmCampaign);
      queryParams.set('utm_content', utm_content);
      queryParams.set('utm_query', utm_query);
      queryParams.set('fbclid', fbclid);
      queryParams.set('gclid', gclid);

      if (source) {
        queryParams.set('source', source);
      }

      queryParams.set('language', this.language);

      if (isOnBoarding) {
        if (!window.location.href.includes('stg')) {
          onBoardingUrl = onBoardingUrl.replace('signup-stg', 'signup');
        }
        // Handle external URL redirection
        const externalUrl = `${onBoardingUrl}?${queryParams.toString()}`;
        const updatedUrl = this.normalizeUrlQuery(externalUrl);
        window.location.href = updatedUrl;
      } else {
        // Handle internal navigation
        this.router.navigate([routeUrl], {
          queryParams: queryParams,
          queryParamsHandling: 'merge',
          fragment: fragment ? fragment : '',
        });
      }
    });
  }

  normalizeUrlQuery(url: string): string {
    let foundQuestionMark = false;

    return url.replace(/\?/g, (match, offset) => {
      if (!foundQuestionMark) {
        foundQuestionMark = true;
        return match;
      }
      return '&';
    });
  }

  private readonly DEVICE_ID_KEY = 'device_id';
  private readonly USER_ID_KEY = 'user_id';

  getDeviceId(): string {
    let deviceId = localStorage.getItem(this.DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId = this.generateUUID();
      localStorage.setItem(this.DEVICE_ID_KEY, deviceId);
    }

    return deviceId;
  }
  getUserId(): string {
    let userId = localStorage.getItem(this.USER_ID_KEY);
    if (!userId) {
      userId = this.generateUUID();
      localStorage.setItem(this.USER_ID_KEY, userId);
    }
    return userId;
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }
  redirectToContactUs() {}
  controlConfirmTryForFree() {
    const language = localStorage.getItem('language');

    const textAlign = language === 'ar' ? 'right' : 'left';

    //  return Swal.fire({
    //      title: this.translate.translate('Alert.ControlLeadTitle'),
    //      text: this.translate.translate('Alert.ControlLeadText'),
    //      html: `
    //          <div style="text-align: ${textAlign};">
    //    1- ${this.translate.translate('Alert.ControlLeadTextMobileAndID')} <br>
    //    2- ${this.translate.translate('Alert.ControlLeadTextRegistrationNumber')}
    //          </div>
    //  `,
    //      // icon: 'question',
    //      confirmButtonColor: '#009877',
    //      denyButtonColor: '#000',
    //      showCancelButton: false,
    //      showDenyButton: true,
    //      confirmButtonText: this.translate.translate('Alert.ControlLeadconfirmButtonText'),
    //      denyButtonText: this.translate.translate('Alert.ControlLeadcancelButtonText'),
    //  });
  }
}

interface Config {
  direction: string;
  language: string;
}

function getWindow(): any {
  return window;
}

@Injectable({
  providedIn: 'root',
})
export class WindowReferenceService {
  get nativeWindow() {
    return getWindow();
  }
}

@Injectable({
  providedIn: 'root',
})
export class DataLayerService {
  private window;

  constructor(_windowRef: WindowReferenceService) {
    this.window = _windowRef.nativeWindow; // intialise the window to what we get from our window service
  }

  private pingHome(obj) {
    if (obj) this.window.dataLayer.push(obj);
  }

  //list of all our dataLayer methods

  logPageView(url) {
    const hit = {
      event: 'content-view',
      pageName: url,
    };
    this.pingHome(hit);
  }

  logEvent(event, category, action, label) {
    const hit = {
      event: event,
      category: category,
      action: action,
      label: label,
    };
    this.pingHome(hit);
  }

  logCustomDimensionTest(value) {
    const hit = {
      event: 'custom-dimension',
      value: value,
    };
    this.pingHome(hit);
  }

  // .. add more custom methods as needed by your app.
}